<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCustomerStore } from '@/stores/CustomersStore'
import { saveBlob } from '@/utils/saveBlob'

/**
 * Attachment list of a single visit, behind one button.
 *
 * Meant for the VisitCard #actions slot — used both on Statistics -> Visits list and in the
 * client card visit history. Renders nothing when the visit has no files.
 */
const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  addLabel: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()
const customerStore = useCustomerStore()

// Per-file spinner, so downloading one attachment does not blank the whole list
const downloading = ref(new Set())

const download = async (file) => {
  if (!file?.id) return

  downloading.value.add(file.id)
  try {
    // downloadFile only returns the blob — saving it is the caller's job
    const blob = await customerStore.downloadFile(file.id)
    if (blob)
      await saveBlob(blob, file.originalName || `file-${file.id}`)
  } catch (error) {
    console.error('Error downloading attachment:', error)
  } finally {
    downloading.value.delete(file.id)
  }
}
</script>

<template>
  <VBtn
    v-if="attachments.length"
    size="small"
    variant="flat"
    color="secondary"
    :title="t('attachments')"
  >
    <template #prepend>
      <VIcon
        size="16"
        class="mr-n2"
        icon="ri-attachment-2"
      />
    </template>
    <template v-if="addLabel">
      <slot name="text" />
    </template>
    <!--
      No presentational classes on VMenu: `class` lands on the `.v-overlay` root, which is a
      full-viewport `position: fixed` box — a border or elevation there paints a giant rectangle
      over the page while the menu is open. Vuetify already shadows the menu's direct .v-sheet
      child, so the styling belongs on VSheet. Use `content-class` if the wrapper ever needs one.
    -->
    <VMenu
      activator="parent"
      :close-on-content-click="false"
    >
      <VSheet
        class="border-sm py-0 xs-font-size"
        style="min-width: 320px; max-width: 320px;"
      >
        <VListItem
          v-for="(file, idx) in attachments"
          :key="idx"
          class="py-0 border-b"
          density="compact"
        >
          <span class="line-height-normal text-break">{{ file.originalName }}</span>
          <template #append>
            <VBtn
              icon
              variant="text"
              color="gray"
              class="ml-2"
              size="small"
              :loading="downloading.has(file.id)"
              :disabled="downloading.size > 0"
              @click="download(file)"
            >
              <VIcon icon="ri-download-line" />
            </VBtn>
          </template>
        </VListItem>
      </VSheet>
    </VMenu>
  </VBtn>
</template>
